import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { finalize } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme.service';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import {
  SlaByPriority,
  SlaSummary,
  TicketAnalysisFilters,
  TicketAnalysisPage,
} from '../../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../../services/ticket-analysis.service';
import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import {
  applyFilterDropdowns,
  createTicketAnalysisFilterConfigMap,
  createTicketAnalysisFiltersForm,
  defaultDateFrom,
  extractBaseFilters,
} from '../ticket-analysis-filters.utils';
import { BAR_CATEGORY_PERCENTAGE, BAR_PERCENTAGE } from '../ticket-analysis-chart.constants';

const DEFAULT_RESPOND_TARGETS: Record<number, number> = { 1: 30, 2: 60, 3: 120, 4: 240, 5: 480 };
const DEFAULT_RESOLVE_TARGETS: Record<number, number> = { 1: 1440, 2: 2880, 3: 4320, 4: 5760, 5: 7200 };

@Component({
  selector: 'ticket-analysis-sla',
  templateUrl: './ticket-analysis-sla.component.html',
  styleUrls: ['./ticket-analysis-sla.component.scss'],
  standalone: false,
})
export class TicketAnalysisSlaComponent implements OnInit, OnDestroy {
  loading = false;
  error = '';
  summary: SlaSummary | null = null;
  expandedPriority: string | null = null;
  expandedTickets: TicketAnalysisPage | null = null;
  expandedTicketsLoading = false;
  expandedPage = 1;
  expandedPageSize = 25;
  expandedChartData: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  barOptions: ChartConfiguration<'bar'>['options'] = {};
  private labelColor = '#555555';
  private ticketsCache = new Map<string, TicketAnalysisPage>();

  readonly slaBarChartPlugins: Plugin[] = [
    {
      id: 'barTopLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i);
          if ((meta as any).type !== 'bar' || meta.hidden) return;
          meta.data.forEach((el, j) => {
            const val = dataset.data[j];
            if (typeof val !== 'number') return;
            const { x, y } = (el as any).getProps(['x', 'y'], true);
            ctx.save();
            ctx.translate(x, y - 10);
            ctx.rotate(-Math.PI / 2);
            ctx.font = '400 12px Inter, sans-serif';
            ctx.fillStyle = this.labelColor;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(val), 0, 0);
            ctx.restore();
          });
        });
      },
    },
  ];

  filtersForm = (() => {
    const form = createTicketAnalysisFiltersForm();
    return form;
  })();
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createTicketAnalysisFilterConfigMap();
  readonly filtersLayoutConfig = new FiltersPanelLayoutConfig({
    elementIds: ['dateRange', 'site', 'board', 'contactName', 'status', 'source'],
    title: 'Filters',
  });

  private readonly STORAGE_KEY = 'ticket_analysis_sla';

  constructor(
    private readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnDestroy(): void {
    Chart.unregister(...this.slaBarChartPlugins);
  }

  ngOnInit(): void {
    this.buildChartOptions();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.buildChartOptions());
    this.restoreFilters();
    this.ticketAnalysisService.exportRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ticketAnalysisService.startExport(this.buildFilters(), { view: 'sla', includeSla: true }));
    this.loadDropdowns();
    this.load();
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      dateRange: { start: defaultDateFrom(), end: new Date() },
      site: null,
      board: null,
      contactName: null,
      status: null,
      source: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.load();
  }

  togglePriority(priority: string, row: SlaByPriority): void {
    if (this.expandedPriority === priority) {
      this.expandedPriority = null;
      this.expandedTickets = null;
    } else {
      this.expandedPriority = priority;
      this.expandedPage = 1;
      this.expandedChartData = this.buildMonthlyChartData(row);
      this.loadExpandedTickets(priority);
    }
  }

  isPriorityExpanded(priority: string): boolean {
    return this.expandedPriority === priority;
  }

  onExpandedPageChange(event: PageEvent): void {
    this.expandedPage = event.pageIndex + 1;
    this.expandedPageSize = event.pageSize;
    if (this.expandedPriority) {
      this.loadExpandedTickets(this.expandedPriority);
    }
  }

  private loadExpandedTickets(priority: string): void {
    const cacheKey = `${priority}:${this.expandedPage}:${this.expandedPageSize}`;
    const cached = this.ticketsCache.get(cacheKey);
    if (cached) {
      this.expandedTickets = cached;
      return;
    }
    this.expandedTicketsLoading = true;
    this.ticketAnalysisService
      .getTicketsForSla(this.buildFilters(), priority, this.expandedPage, this.expandedPageSize)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.expandedTicketsLoading = false)),
      )
      .subscribe({
        next: (data) => {
          this.ticketsCache.set(cacheKey, data);
          this.expandedTickets = data;
        },
        error: () => (this.expandedTickets = null),
      });
  }

  formatDuration(mins: number | null): string {
    if (mins === null) return '—';
    const totalMins = Math.round(mins);
    const d = Math.floor(totalMins / 1440);
    const h = Math.floor((totalMins % 1440) / 60);
    const m = totalMins % 60;
    const parts: string[] = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || parts.length === 0) parts.push(`${m}m`);
    return parts.join(' ');
  }

  effectiveRespondTarget(row: SlaByPriority): number | null {
    if (row.targetResponseTimeMins != null) return row.targetResponseTimeMins;
    const n = this.priorityNumber(row.priority);
    return n != null ? (DEFAULT_RESPOND_TARGETS[n] ?? null) : null;
  }

  effectiveResolveTarget(row: SlaByPriority): number | null {
    if (row.targetResolveTimeMins != null) return row.targetResolveTimeMins;
    const n = this.priorityNumber(row.priority);
    return n != null ? (DEFAULT_RESOLVE_TARGETS[n] ?? null) : null;
  }

  statCellClass(colorClass: string | undefined): string {
    const map: Record<string, string> = {
      'sla__value--info': 'sla__stat-cell--info',
      'sla__value--green': 'sla__stat-cell--green',
      'sla__value--orange': 'sla__stat-cell--orange',
      'sla__value--red': 'sla__stat-cell--red',
    };
    return map[colorClass] ?? '';
  }

  slaStatusIcon(colorClass: string): string {
    if (!colorClass) return '';
    return colorClass === 'sla__value--green' ? 'check_circle' : 'cancel';
  }

  responseTimeColor(avg: number | null | undefined, target: number | null | undefined): string {
    if (avg == null) return '';
    if (target == null) return 'sla__value--info';
    if (avg <= target) return 'sla__value--green';
    if (avg <= target * 1.2) return 'sla__value--orange';
    return 'sla__value--red';
  }

  resolveTimeColor(avg: number | null | undefined, target: number | null | undefined): string {
    if (avg == null) return '';
    if (target == null) return 'sla__value--info';
    if (avg <= target) return 'sla__value--green';
    if (avg <= target * 1.2) return 'sla__value--orange';
    return 'sla__value--red';
  }

  private load(): void {
    this.loading = true;
    this.error = '';
    this.expandedPriority = null;
    this.expandedTickets = null;
    this.expandedChartData = { labels: [], datasets: [] };
    this.ticketsCache.clear();
    this.ticketAnalysisService
      .getSla(this.buildFilters())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (data) => (this.summary = data),
        error: () => (this.error = 'Failed to load SLA data.'),
      });
  }

  private loadDropdowns(): void {
    this.ticketAnalysisService.getFilters().subscribe({
      next: (response) => {
        this.filtersConfigMap = applyFilterDropdowns(response, this.filtersConfigMap);
      },
    });
  }

  private buildFilters(): TicketAnalysisFilters {
    return extractBaseFilters(this.valuesMap);
  }

  private saveFilters(): void {
    const dateRange = this.valuesMap?.dateRange as { start: Date; end: Date } | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      dateRange: dateRange
        ? { start: dateRange.start?.toISOString?.() ?? null, end: dateRange.end?.toISOString?.() ?? null }
        : null,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawRange = saved['dateRange'] as { start: string; end: string } | null;
    const start = rawRange?.start ? new Date(rawRange.start) : defaultDateFrom();
    const end = rawRange?.end ? new Date(rawRange.end) : new Date();
    const asTree = (v: unknown) =>
      v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;
    const asBoard = (v: unknown) => (Array.isArray(v) && v.length > 0 ? v : null);
    const restored = {
      dateRange: { start, end },
      contactName: (saved['contactName'] as string | null) ?? null,
      board: asBoard(saved['board']),
      site: asTree(saved['site']),
      status: asTree(saved['status']),
      source: asTree(saved['source']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
  }

  private priorityNumber(priority: string): number | null {
    const match = priority.match(/(\d)/);
    return match ? parseInt(match[1]) : null;
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.labelColor = isDark ? '#F1F1F1' : '#555555';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    this.barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 44 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: {
        x: { ticks: { color: this.labelColor }, grid: { color: gridColor } },
        y: { ticks: { color: this.labelColor }, grid: { color: gridColor }, beginAtZero: true },
      },
    };
  }

  private buildMonthlyChartData(row: SlaByPriority): ChartConfiguration<'bar'>['data'] {
    const months = row.byMonth ?? [];
    const labels = months.map((m, i) => {
      const [year, month] = m.month.split('-');
      const prev = i > 0 ? months[i - 1]!.month.split('-')[0] : null;
      const name = new Date(+year!, +month! - 1).toLocaleString('en-US', { month: 'short' }).toUpperCase();
      return prev !== year ? `${name} '${year!.slice(2)}` : name;
    });
    return {
      labels,
      datasets: [
        {
          label: 'Tickets',
          data: months.map((m) => m.count),
          backgroundColor: '#21b3ff',
          borderRadius: 3,
          barPercentage: BAR_PERCENTAGE,
          categoryPercentage: BAR_CATEGORY_PERCENTAGE,
        },
      ],
    };
  }
}
